/**
 * Los ids llegan como texto y no como número: la API los serializa así para no perder
 * precisión al pasar un Snowflake por JavaScript. Tratarlos siempre como string.
 */

export const PlanVisibility = {
  Public: 'Public',
  Private: 'Private',
} as const;

export type PlanVisibility = (typeof PlanVisibility)[keyof typeof PlanVisibility];

export const PlanSyncStatus = {
  NotSynced: 'NotSynced',
  Synced: 'Synced',
  Pending: 'Pending',
  Failed: 'Failed',
} as const;

export type PlanSyncStatus = (typeof PlanSyncStatus)[keyof typeof PlanSyncStatus];

export interface Plan {
  id: string;
  name: string;
  description: string | null;
  priceMonthly: number;
  priceAnnual: number;
  isActive: boolean;
  visibility: PlanVisibility;
  syncStatus: PlanSyncStatus;
  /**
   * Un plan puede estar activo y aun así no ofrecerse: sin precios en Stripe no hay con qué
   * cobrarle a nadie. `isActive` es la intención; esto es lo que de verdad pasa.
   */
  isOfferable: boolean;
  lastSyncedAt: string | null;
}

export interface PlanDetail extends Plan {
  maxUsers: number;
  maxRegions: number;
  maxActiveTopics: number;
  maxActiveActors: number;
  maxAiAnalysisMonthly: number;
  maxAlerts: number;
  maxScheduledReports: number;
  stripeProductId: string | null;
  stripeMonthlyPriceId: string | null;
  stripeAnnualPriceId: string | null;
  syncError: string | null;
  createdAt: string;
  updatedAt: string | null;
}

export interface PlanLimits {
  maxUsers: number;
  maxRegions: number;
  maxActiveTopics: number;
  maxActiveActors: number;
  maxAiAnalysisMonthly: number;
  maxAlerts: number;
  maxScheduledReports: number;
}

export interface CreatePlanRequest extends PlanLimits {
  name: string;
  description?: string;
  priceMonthly: number;
  priceAnnual: number;
  visibility: PlanVisibility;
}

/** Sin precios a proposito: los cambia `PUT /admin/plans/{id}/pricing`. */
export interface UpdatePlanDetailsRequest extends PlanLimits {
  name: string;
  description?: string;
}

/**
 * Informativo antes de cambiar un precio: dice a cuantas cuentas les cambia la oferta
 * futura, que no es lo mismo que cambiarles el cobro.
 */
export interface PlanPricingImpact {
  planId: string;
  planName: string;
  currentPriceMonthly: number;
  currentPriceAnnual: number;
  activeSubscriptions: number;
  keepsCurrentPrice: boolean;
}

export interface UpdatePlanPricingRequest {
  priceMonthly: number;
  priceAnnual: number;
  /** Desactivado por omision: un error de captura no debe cambiarle el cobro a nadie. */
  applyToExistingSubscribers: boolean;
}

export const PlanPriceMigrationStatus = {
  Scheduled: 'Scheduled',
  Completed: 'Completed',
  Cancelled: 'Cancelled',
} as const;

export type PlanPriceMigrationStatus =
  (typeof PlanPriceMigrationStatus)[keyof typeof PlanPriceMigrationStatus];

export const PlanPriceMigrationAccountStatus = {
  Pending: 'Pending',
  Applied: 'Applied',
  Cancelled: 'Cancelled',
  Failed: 'Failed',
} as const;

export type PlanPriceMigrationAccountStatus =
  (typeof PlanPriceMigrationAccountStatus)[keyof typeof PlanPriceMigrationAccountStatus];

export interface PlanPriceMigrationAccount {
  tenantId: string;
  subscriptionId: string;
  status: PlanPriceMigrationAccountStatus;
  attemptCount: number;
  failureReason: string | null;
  noticeSentAt: string | null;
  resolvedAt: string | null;
}

export interface PlanPriceMigration {
  id: string;
  planId: string;
  oldPriceMonthly: number;
  oldPriceAnnual: number;
  newPriceMonthly: number;
  newPriceAnnual: number;
  effectiveAt: string;
  status: PlanPriceMigrationStatus;
  initiatedByAdminId: string;
  cancellationReason: string | null;
  createdAt: string;
  completedAt: string | null;
  totalAccounts: number;
  pendingCount: number;
  appliedCount: number;
  cancelledCount: number;
  failedCount: number;
  accounts: PlanPriceMigrationAccount[];
}
